import React, { useEffect, useState } from "react";
import { styled } from "@mui/material";
import {
    Routes,
    Route,
    useLocation,
    Navigate,
} from "react-router-dom";

import {
    AccountPage,
    ContactUsPage,
    ExistingCustomerContactUsPage,
    ExistingCustomerPage,
    FacilitySelectWrapper,
    LandingPage,
    NewCustomerPage,
} from "../pages";
import { MoveOutContextProvider } from "../providers/moveout-provider/MoveOutProvider";
import { PageLayout } from "../PageLayout";
import { PaymentsContextProvider } from "../providers/payments-provider/PaymentsProvider";
import { GoatSecuritySmsVerificationWrapper, SmsVerificationWrapper } from "../wrappers/sms-verification/SmsVerificationWrapper";
import AnalyticsProvider from "../providers/AnalyticsProvider";
import { mainRoutes } from "./routes.ts"
import { ErrorBoundary, ExceptionPage } from "../utils/errorHandling.tsx";
import AuctionBuyerRoutes from "../pages/auction-buyer/AuctionBuyerRoutes.tsx";
import NewMoveInAccountPage from "../pages/account/new-move-in/NewMoveInAccountPage.tsx";
import { useFacilityIdParam } from "../providers/facility-provider/[hooks]/useFacilityIdParam.tsx";

export const PaymentRoutes = React.lazy(() => import('../pages/payments/PaymentsRoutes'));
const AccessRoutes = React.lazy(() => import('../pages/access/AccessRoutes'));
const MoveoutRoutes = React.lazy(() => import('../pages/moveout/MoveoutRoutes'));

export const useAnalyticsRoute = (Component: () => JSX.Element, pageName: string) => {
    return (
        <ErrorBoundary fallback={ExceptionPage}>
            <AnalyticsProvider pageName={pageName}>
                <Component />
            </AnalyticsProvider>
        </ErrorBoundary>
    )
}

const AppRouter = () => {
    const { pathname, search } = useLocation()
    const [tryAgain, setTryAgain] = useState(true)

    useEffect(() => {
        window.scrollTo(0, 0);
        try {
            const swizl = document.getElementsByClassName("sce-Dot")[0] as HTMLDivElement

            if (!swizl) {
                if (tryAgain) {
                    setTimeout(() => setTryAgain(false), 500)
                }
                return;
            }

            if (swizl && swizl.style) {
                swizl.style.display = "none"
                swizl.style.top = "0px"
            }
        } catch {

        }
    }, [pathname, search, tryAgain])

    return (
        <PageLayout noBackButton={pathname === "/"}>
            <Section>
                <Routes>
                    <Route index element={useAnalyticsRoute(LandingPage, "Landing")} />
                    <Route
                        path={mainRoutes.NewCustomer}
                        element={useAnalyticsRoute(() => (
                            <FacilitySelectWrapper>
                                <NewCustomerPage />
                            </FacilitySelectWrapper>
                        ), "NewCustomer")} />

                    <Route
                        path={mainRoutes.ContactUs}
                        element={useAnalyticsRoute(() => (
                            <FacilitySelectWrapper>
                                <ContactUsPage />
                            </FacilitySelectWrapper>
                        ), "NewCustomer/ContactUs")} />
                    <Route
                        path={mainRoutes.ExistingCustomer + mainRoutes.ContactUs}
                        element={useAnalyticsRoute(() => (
                            <FacilitySelectWrapper>
                                <ExistingCustomerContactUsPage />
                            </FacilitySelectWrapper>
                        ), "ExistingCustomer/ContactUs")} />

                    <Route
                        path={mainRoutes.ExistingCustomer}
                        element={useAnalyticsRoute(() => (
                            <FacilitySelectWrapper>
                                <ExistingCustomerPage />
                            </FacilitySelectWrapper>
                        ), "ExistingCustomer")} />


                    <Route path={mainRoutes.Access} element={
                        <GoatSecuritySmsVerificationWrapper>
                            <FacilitySelectWrapper>
                                <React.Suspense fallback={<div>Loading...</div>}>
                                    <AccessRoutes />
                                </React.Suspense>
                            </FacilitySelectWrapper>
                        </GoatSecuritySmsVerificationWrapper>
                    } />

                    <Route path={mainRoutes.Payments} element={
                        <SmsVerificationWrapper>
                            <FacilitySelectWrapper>
                                <PaymentsContextProvider>
                                    <React.Suspense fallback={<div>Loading...</div>}>
                                        <PaymentRoutes />
                                    </React.Suspense>
                                </PaymentsContextProvider>
                            </FacilitySelectWrapper>
                        </SmsVerificationWrapper>
                    } />

                    <Route path={mainRoutes.Account} element={useAnalyticsRoute(() =>
                        <SmsVerificationWrapper>
                            <AccountPage />
                        </SmsVerificationWrapper>, "Account")}
                    />

                    <Route path={mainRoutes.Account + "/new-move-in"} element={useAnalyticsRoute(() =>
                        <SmsVerificationWrapper>
                            <NewMoveInAccountPage />
                        </SmsVerificationWrapper>, "Account")}
                    />

                    <Route path={mainRoutes.MoveOut} element={
                        <FacilitySelectWrapper>
                            <MoveOutFeatureGuard>
                                <MoveOutContextProvider>
                                    <React.Suspense fallback={<div>Loading...</div>}>
                                        <MoveoutRoutes />
                                    </React.Suspense>
                                </MoveOutContextProvider>
                            </MoveOutFeatureGuard>
                        </FacilitySelectWrapper>
                    } />

                    <Route path={mainRoutes.AuctionBuyer + "/*"} element={
                        <AuctionBuyerFeatureGuard>
                            <SmsVerificationWrapper>
                                <React.Suspense fallback={<div>Loading...</div>}>
                                    <AuctionBuyerRoutes />
                                </React.Suspense>
                            </SmsVerificationWrapper>
                        </AuctionBuyerFeatureGuard>
                    } />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Section>
        </PageLayout >
    )
}

const MoveOutFeatureGuard = ({ children }: { children: React.ReactNode }) => {
    const [facilityId] = useFacilityIdParam()

    if (_context.companySettings.isMoveOutEnabled) {
        return children;
    }

    return <Navigate replace to={`/?facilityId=${facilityId}`} />
}

const AuctionBuyerFeatureGuard = ({ children }: { children: React.ReactNode }) => {
    const [facilityId] = useFacilityIdParam()

    if (_context.companySettings.isAuctionBuyerEnabled) {
        return children;
    }

    return <Navigate replace to={`/?facilityId=${facilityId}`} />
}

export const Section = styled("div")({
    padding: "0 24px",
    maxWidth: "475px",
    margin: "auto",
})

export default AppRouter;
